import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { useAuth } from '../hooks/useAuth';
import ShieldIcon from '../components/ShieldIcon';
import LoginForm from '../components/LoginForm';
import SignupForm from '../components/SignupForm';
import VerifyForm from '../components/VerifyForm';
import ForgotPasswordForm from '../components/ForgotPasswordForm';
import ResetPasswordForm from '../components/ResetPasswordForm';
import { brand } from '../config/brand';

type View = 'login' | 'signup' | 'verify' | 'forgot' | 'reset';

export default function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('ext') === '1') {
      import('aws-amplify/auth').then(({ fetchAuthSession }) => {
        fetchAuthSession().then((session) => {
          const idToken = session.tokens?.idToken?.toString();
          const refreshKey = Object.keys(localStorage).find((k) => k.endsWith('.refreshToken'));
          const refreshToken = refreshKey ? localStorage.getItem(refreshKey) : null;

          const payload = {
            type: 'ALIV_EXT_TOKENS',
            idToken,
            refreshToken,
            email: user.signInDetails?.loginId,
          };

          window.postMessage(payload, window.location.origin);
          const interval = setInterval(() => {
            window.postMessage(payload, window.location.origin);
          }, 500);

          setTimeout(() => {
            clearInterval(interval);
            navigate('/dashboard', { replace: true });
          }, 10000);
        });
      });
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  if (user) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ext') === '1') {
      return (
        <div className="loading-page">
          <p>Signing in to extension...</p>
        </div>
      );
    }
    return null;
  }

  function switchView(v: View) {
    setError('');
    setMessage('');
    setView(v);
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        navigate('/dashboard', { replace: true });
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        setView('verify');
      }
    } catch (err: any) {
      setError(err.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      if (result.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setMessage('Check your email for a verification code.');
        setView('verify');
      }
    } catch (err: any) {
      setError(err.message ?? 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      setMessage('Email verified! Please sign in.');
      setView('login');
      setCode('');
    } catch (err: any) {
      setError(err.message ?? 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setMessage('Check your email for a reset code.');
      setView('reset');
    } catch (err: any) {
      setError(err.message ?? 'Password reset failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      setMessage('Password reset! Please sign in.');
      setView('login');
      setCode('');
      setNewPassword('');
    } catch (err: any) {
      setError(err.message ?? 'Password reset failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-mobile-bar">
        <ShieldIcon size={22} />
        {brand.name}
      </div>

      <div className="login-brand">
        <div className="login-brand-top">
          <div className="login-wordmark">
            <ShieldIcon size={32} />
            {brand.name}
          </div>
        </div>
        <div className="login-brand-center">
          <h1 className="login-tagline">{brand.tagline}</h1>
          <p className="login-tagline-sub">{brand.description}</p>
        </div>
        <div className="login-brand-bottom">
          <span className="login-trust-badge">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            256-bit encrypted
          </span>
          <span className="login-trust-badge">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            No biometric data stored
          </span>
          <span className="login-trust-badge">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            Powered by AWS Rekognition
          </span>
        </div>
      </div>

      <div className="login-form-panel">
        <div className="auth-card">
          {message && <p className="success-msg">{message}</p>}
          {error && <p className="error-msg">{error}</p>}

          {view === 'login' && (
            <LoginForm
              email={email}
              password={password}
              loading={loading}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onSubmit={handleLogin}
              onSwitchView={switchView}
            />
          )}

          {view === 'signup' && (
            <SignupForm
              email={email}
              password={password}
              loading={loading}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onSubmit={handleSignUp}
              onSwitchView={switchView}
            />
          )}

          {view === 'verify' && (
            <VerifyForm
              email={email}
              code={code}
              loading={loading}
              onCodeChange={setCode}
              onSubmit={handleVerify}
              onSwitchView={switchView}
            />
          )}

          {view === 'forgot' && (
            <ForgotPasswordForm
              email={email}
              loading={loading}
              onEmailChange={setEmail}
              onSubmit={handleForgotPassword}
              onSwitchView={switchView}
            />
          )}

          {view === 'reset' && (
            <ResetPasswordForm
              code={code}
              newPassword={newPassword}
              loading={loading}
              onCodeChange={setCode}
              onNewPasswordChange={setNewPassword}
              onSubmit={handleResetPassword}
              onSwitchView={switchView}
            />
          )}
        </div>
      </div>
    </div>
  );
}
